import React, { useState } from 'react';
import { DB } from '../data/database';
import { ROLES } from '../data/constants';
import { formatPrice } from '../utils/currency';
import { useLanguage } from '../translations/LanguageContext';
import { PackageIcon, CheckIcon, XIcon, CarIcon, EngineIcon, PartsIcon } from './Icons';
import { logActivity } from '../data/notifications';
import centralUpdateStatus from '../utils/updateUnitStatus';
import CustomerFilter, { CustomerFilterBanner } from './CustomerFilter';

const STATUS_COLORS = {
  submitted: 'pending', under_review: 'transit', approved: 'available',
  partially_approved: 'assigned', rejected: 'sold', paid: 'available',
  shipped: 'transit', completed: 'available', cancelled: 'sold', draft: 'pending',
};

function SelectionOrders({ user, lang, custFilter, setCustFilter }) {
  const { t } = useLanguage();
  const role = ROLES[user.role] || {};
  const [expandedId, setExpandedId] = useState(null);
  const [rejectingUnit, setRejectingUnit] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editPrices, setEditPrices] = useState({});
  const [adminNotes, setAdminNotes] = useState({});
  const [, forceUpdate] = useState();

  const allOrders = DB.get('selectionOrders') || [];
  const customers = DB.get('customers') || [];
  const orders = custFilter ? allOrders.filter(o => o.customerId === custFilter) : allOrders;

  // Sort: submitted first, then by date
  const sorted = [...orders].sort((a, b) => {
    const priority = { submitted: 0, under_review: 1, partially_approved: 2, approved: 3, paid: 4, shipped: 5, completed: 6, rejected: 7, cancelled: 8, draft: 9 };
    const pa = priority[a.status] ?? 10;
    const pb = priority[b.status] ?? 10;
    if (pa !== pb) return pa - pb;
    return (b.submittedAt || b.createdAt || '').localeCompare(a.submittedAt || a.createdAt || '');
  });

  const getCustomer = (id) => customers.find(c => c.id === id);

  const getUnitIcon = (type) =>
    type === 'vehicle' ? <CarIcon /> : type === 'engine' ? <EngineIcon /> : <PartsIcon />;

  const genInvoiceNum = () => DB.nextInvoiceNumberAsync();

  // Approve a single unit in an order
  // Check if a unit has an approved FOB price for this customer
  const getCustomerFob = (customerId, unitId) => {
    const quotes = DB.get('priceQuotes') || [];
    for (const q of quotes) {
      if ((q.customerId === customerId || q.requestedBy === customerId) && ['approved', 'partially_approved'].includes(q.status) && Array.isArray(q.units)) {
        const match = q.units.find(u => u.unit_id === unitId && u.admin_approved);
        if (match && (match.fob_price || match.pricing?.fob_price)) return match.fob_price || match.pricing?.fob_price;
      }
    }
    return null;
  };

  const approveUnit = (order, unitIndex) => {
    const unit = order.units[unitIndex];

    // Check FOB price exists
    const fob = unit.fob_price_usd || unit.modified_price || getCustomerFob(order.customerId, unit.unit_id);
    if (!fob) {
      alert(`Cannot approve — ${unit.unit_description || unit.unit_sku} has no FOB price. Send to accountant for pricing first.`);
      return;
    }

    const updatedUnits = [...order.units];
    updatedUnits[unitIndex] = {
      ...updatedUnits[unitIndex],
      status: 'approved',
      approved_by: user.id,
      approved_at: new Date().toISOString(),
      fob_price_usd: fob,
    };

    // Check if already sold to another customer
    const key = unit.unit_type === 'vehicle' ? 'vehicles' : unit.unit_type === 'engine' ? 'engines' : 'spareParts';
    const existingUnit = (DB.get(key) || []).find(u => u.id === unit.unit_id);
    if (existingUnit?.status === 'sold' && existingUnit?.assignedTo && existingUnit.assignedTo !== order.customerId) {
      alert(`Cannot approve — this unit is already sold to another customer.`);
      return;
    }

    // NOW set assignedTo + status sold (this is when ownership transfers)
    updateUnitStatusFn(unit.unit_id, unit.unit_type, 'sold', order.customerId, fob);

    saveOrderUnits(order, updatedUnits);
  };

  // Reject a single unit (called after reason is entered)
  const rejectUnit = (order, unitIndex, reason) => {
    const updatedUnits = [...order.units];
    updatedUnits[unitIndex] = {
      ...updatedUnits[unitIndex],
      status: 'rejected',
      admin_notes: reason || '',
    };
    clearUnitSelection(updatedUnits[unitIndex].unit_id, updatedUnits[unitIndex].unit_type);
    saveOrderUnits(order, updatedUnits);
    setRejectingUnit(null);
    setRejectReason('');
  };

  // Approve all pending units
  const approveAll = (order) => {
    // Check all pending units have FOB prices
    const pending = (order.units || []).filter(u => u.status === 'pending');
    const unpriced = pending.filter(u => {
      const fob = u.fob_price_usd || u.modified_price || getCustomerFob(order.customerId, u.unit_id);
      return !fob;
    });
    if (unpriced.length > 0) {
      alert(`Cannot approve — ${unpriced.length} unit${unpriced.length !== 1 ? 's have' : ' has'} no FOB price:\n${unpriced.map(u => u.unit_description || u.unit_sku).join('\n')}\n\nSend to accountant for pricing first.`);
      return;
    }

    const updatedUnits = ((order.units) || []).map(u => {
      if (u.status === 'pending') {
        const key = u.unit_type === 'vehicle' ? 'vehicles' : u.unit_type === 'engine' ? 'engines' : 'spareParts';
        const existing = (DB.get(key) || []).find(x => x.id === u.unit_id);
        if (existing?.status === 'sold' && existing?.assignedTo !== order.customerId) {
          return { ...u, status: 'rejected', admin_notes: 'Already sold to another customer' };
        }
        const fob = u.fob_price_usd || u.modified_price || getCustomerFob(order.customerId, u.unit_id);
        updateUnitStatusFn(u.unit_id, u.unit_type, 'sold', order.customerId, fob);
        return { ...u, status: 'approved', fob_price_usd: fob, approved_by: user.id, approved_at: new Date().toISOString() };
      }
      return u;
    });
    saveOrderUnits(order, updatedUnits);
  };

  // Reject all pending units
  const rejectAll = (order) => {
    if (!window.confirm('Reject all pending units in this order?')) return;
    const updatedUnits = ((order.units) || []).map(u => {
      if (u.status === 'pending') {
        clearUnitSelection(u.unit_id, u.unit_type);
        return { ...u, status: 'rejected' };
      }
      return u;
    });
    saveOrderUnits(order, updatedUnits);
  };

  // Save updated units back to order + recalculate status
  const saveOrderUnits = async (order, updatedUnits) => {
    const approved = updatedUnits.filter(u => u.status === 'approved').length;
    const rejected = updatedUnits.filter(u => u.status === 'rejected').length;
    const pending = updatedUnits.filter(u => u.status === 'pending').length;
    const total = updatedUnits.length;

    let newStatus = order.status;
    if (pending === 0) {
      if (approved === total) newStatus = 'approved';
      else if (rejected === total) newStatus = 'rejected';
      else newStatus = 'partially_approved';
    } else {
      newStatus = 'under_review';
    }

    const updatedOrder = {
      ...order,
      units: updatedUnits,
      status: newStatus,
      reviewedBy: user.id,
      reviewedAt: new Date().toISOString(),
    };

    const allOrders = DB.get('selectionOrders') || [];
    DB.set('selectionOrders', allOrders.map(o => o.id === order.id ? updatedOrder : o));

    // Auto-generate proforma invoice if fully approved or partially approved (for approved units)
    if (pending === 0 && approved > 0 && !order.invoiceId) {
      await generateProformaInvoice(updatedOrder, updatedUnits.filter(u => u.status === 'approved'));
    }

    // Notify customer with specific messages based on outcome
    if (pending === 0) {
      if (approved === total) {
        logActivity.selectionApproved(order.id, order.customerName, approved, order.customerId);
      } else if (rejected === total) {
        logActivity.selectionRejected(order.id, order.customerName, order.customerId);
      } else {
        logActivity.selectionPartial(order.id, order.customerName, approved, total, order.customerId);
      }
    }

    forceUpdate({});
  };

  // Update a unit's status via central function (handles all ripple effects)
  const updateUnitStatusFn = (unitId, unitType, newStatus, customerId, agreedFob) => {
    const type = unitType === 'vehicle' ? 'vehicle' : unitType === 'engine' ? 'engine' : 'part';
    centralUpdateStatus(unitId, type, newStatus, {
      assignedTo: customerId || null,
      fobPrice: agreedFob || null,
      updatedBy: user.name,
    });
  };

  // Clear selection when a unit is rejected (unit stays available, stays in customer stock)
  const clearUnitSelection = (unitId, unitType) => {
    const key = unitType === 'vehicle' ? 'vehicles' : unitType === 'engine' ? 'engines' : 'spareParts';
    const list = DB.get(key) || [];
    DB.set(key, list.map(u => u.id === unitId ? { ...u, selectionOrderId: '' } : u));
  };

  // Load fee defaults
  const getFeeDefaults = () => {
    try {
      const cfg = DB.get('appConfig');
      if (cfg && typeof cfg === 'object' && !Array.isArray(cfg)) return cfg.fee_defaults || {};
      if (Array.isArray(cfg)) { const r = cfg.find(x => x.key === 'fee_defaults'); return r?.value || {}; }
    } catch {} return {};
  };

  // Generate proforma invoice with fees + customer payment terms
  const generateProformaInvoice = async (order, approvedUnits) => {
    const invoices = DB.get('invoices') || [];
    const invoiceNumber = await genInvoiceNum();
    const customer = getCustomer(order.customerId);
    const fees = getFeeDefaults();

    // Build line items with fee breakdown
    const feesBreakdown = approvedUnits.map(u => {
      const fob = u.fob_price_usd || u.modified_price || 0;
      const flatPct = fees.flat_charge_pct || 0;
      const flatAmt = Math.round(fob * flatPct / 100 * 100) / 100;
      const recyclePct = fees.recycle_fee_pct || 0;
      const recycleAmt = Math.round(fob * recyclePct / 100 * 100) / 100;
      const vatPct = fees.vat_pct || 0;
      const vatAmt = Math.round(fob * vatPct / 100 * 100) / 100;
      const unitTotal = fob + flatAmt + recycleAmt + vatAmt;
      return {
        unit_id: u.unit_id, unit_sku: u.unit_sku,
        fob_price: fob,
        flat_charge_pct: flatPct, flat_charge_amount: flatAmt,
        recycle_fee_pct: recyclePct, recycle_fee_amount: recycleAmt,
        vat_pct: vatPct, vat_amount: vatAmt,
        other_fees: [],
        unit_total: Math.round(unitTotal * 100) / 100,
      };
    });

    const lineItems = approvedUnits.map((u, i) => ({
      unit_id: u.unit_id, unit_sku: u.unit_sku,
      description: u.unit_description,
      fob_price: u.fob_price_usd || u.modified_price || 0,
      fees: feesBreakdown[i],
      total: feesBreakdown[i].unit_total,
    }));

    const subtotal = approvedUnits.reduce((s, u) => s + (u.fob_price_usd || 0), 0);
    const totalFees = feesBreakdown.reduce((s, f) => s + f.flat_charge_amount + f.recycle_fee_amount + f.vat_amount, 0);
    const total = Math.round((subtotal + totalFees) * 100) / 100;

    // Customer payment terms → milestones
    const terms = customer?.paymentTerms?.milestones || customer?.payment_terms?.milestones || [
      { id: 'first_payment', label: 'Before shipping', percentage: 50 },
      { id: 'final_payment', label: 'Before BL surrender', percentage: 50 },
    ];
    const today = new Date();
    const paymentMilestones = terms.map((m, i) => {
      const amount = Math.round(total * (m.percentage || 0) / 100 * 100) / 100;
      const dueDate = new Date(today);
      dueDate.setDate(dueDate.getDate() + (i === 0 ? 7 : 30 * (i + 1)));
      return {
        milestone: m.id || `ms_${i}`,
        label: m.label,
        percentage: m.percentage,
        amount,
        status: 'pending',
        due_date: dueDate.toISOString().split('T')[0],
        paid_date: null,
        receipt_url: null,
        verified_by: null,
        verified_at: null,
      };
    });

    // Handle deposit if required
    if (customer?.depositRequired || customer?.deposit_required) {
      const depPct = customer.depositPercentage || customer.deposit_percentage || 10;
      const depAmt = Math.round(total * depPct / 100 * 100) / 100;
      paymentMilestones.unshift({
        milestone: 'deposit', label: `Deposit (${depPct}%)`, percentage: depPct,
        amount: depAmt, status: 'pending',
        due_date: new Date().toISOString().split('T')[0],
        paid_date: null, receipt_url: null, verified_by: null, verified_at: null,
      });
    }

    const invoice = {
      id: DB.genId('INV'),
      invoiceNumber,
      customerId: order.customerId,
      customerName: order.customerName,
      invoiceType: 'sale',
      invoiceCategory: 'proforma',
      lineItems,
      feesBreakdown,
      subtotal,
      tax: totalFees,
      total,
      currency: customer?.currency || 'USD',
      customerCurrency: customer?.currency || 'USD',
      status: 'draft',
      issuedDate: new Date().toISOString().split('T')[0],
      amountPaid: 0,
      balanceDue: total,
      paymentMilestones,
      paymentTermsSnapshot: terms,
      linkedSelectionOrderId: order.id,
      createdBy: user.id,
      createdAt: new Date().toISOString().split('T')[0],
    };

    DB.set('invoices', [...invoices, invoice]);

    // Link invoice to order
    const allOrders = DB.get('selectionOrders') || [];
    DB.set('selectionOrders', allOrders.map(o => o.id === order.id ? { ...o, invoiceId: invoice.id } : o));

    // Notify accountant about draft invoice (NOT customer — customer gets notified when accountant sends it)
    logActivity.invoiceCreated(invoiceNumber, order.customerName, total, user.name);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h2>{t('nav_selection_orders')}</h2>
        <CustomerFilter value={custFilter} onChange={setCustFilter} />
      </div>
      <CustomerFilterBanner value={custFilter} onChange={setCustFilter} />

      {sorted.length === 0 ? (
        <div className="empty">
          <PackageIcon />
          <div className="empty-title">{t('selorder_no_orders')}</div>
          <div className="empty-text">{t('selorder_empty_msg')}</div>
        </div>
      ) : sorted.map(order => {
        const customer = getCustomer(order.customerId);
        const isExpanded = expandedId === order.id;
        const pendingCount = (Array.isArray(order.units) ? order.units : []).filter(u => u.status === 'pending').length;
        const approvedCount = (Array.isArray(order.units) ? order.units : []).filter(u => u.status === 'approved').length;
        const rejectedCount = (Array.isArray(order.units) ? order.units : []).filter(u => u.status === 'rejected').length;
        const needsAction = order.status === 'submitted' || pendingCount > 0;

        return (
          <div key={order.id} className="card" style={{ marginBottom: 12, borderColor: needsAction ? 'var(--blue)' : undefined }}>
            {/* Header */}
            <div className="card-body" style={{ cursor: 'pointer', padding: '14px 20px' }}
              onClick={() => setExpandedId(isExpanded ? null : order.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>{order.customerName}</span>
                    <span className={`status status-${STATUS_COLORS[order.status] || 'pending'}`} style={{ fontSize: 10 }}>
                      {(order.status || '').replace(/_/g, ' ')}
                    </span>
                    {needsAction && (
                      <span style={{ fontSize: 10, background: 'var(--blue)', color: '#fff', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
                        Action Required
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                    {order.submittedAt ? (() => {
                      const ms = Date.now() - new Date(order.submittedAt).getTime();
                      const h = Math.floor(ms / 3600000);
                      return h < 1 ? 'just now' : h < 24 ? `${h}h ago` : `${Math.floor(h/24)}d ago`;
                    })() : ''}
                    {' · '}#{order.id}
                    {customer?.country ? ` · ${customer.country}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexShrink: 0 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--blue)' }}>{order.totalUnits || (Array.isArray(order.units) ? order.units : []).length}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>Units</div>
                  </div>
                  <span style={{ fontSize: 14, color: 'var(--text3)', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
                </div>
              </div>
            </div>

            {/* Expanded: unit list + actions */}
            {isExpanded && (
              <div style={{ padding: '0 20px 16px', borderTop: '1px solid var(--border)' }}>
                {/* Bulk actions */}
                {pendingCount > 0 && (
                  <div style={{ display: 'flex', gap: 8, padding: '12px 0', borderBottom: '1px solid var(--border)', marginBottom: 8 }}>
                    <button className="btn btn-success btn-sm" onClick={() => approveAll(order)}>
                      <CheckIcon />Approve All ({pendingCount})
                    </button>
                    <button className="btn btn-secondary btn-sm" style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                      onClick={() => rejectAll(order)}>
                      <XIcon />Reject All
                    </button>
                  </div>
                )}

                {/* Status summary */}
                {(approvedCount > 0 || rejectedCount > 0) && (
                  <div style={{ display: 'flex', gap: 12, marginBottom: 10, fontSize: 12 }}>
                    {approvedCount > 0 && <span style={{ color: 'var(--green)', fontWeight: 600 }}>✓ {approvedCount} approved</span>}
                    {rejectedCount > 0 && <span style={{ color: 'var(--red)', fontWeight: 600 }}>✕ {rejectedCount} rejected</span>}
                    {pendingCount > 0 && <span style={{ color: 'var(--yellow)', fontWeight: 600 }}>⏳ {pendingCount} pending</span>}
                  </div>
                )}

                {/* Customer notes */}
                {order.customerNotes && (
                  <div style={{ padding: '8px 12px', background: 'var(--bg3)', borderRadius: 8, marginBottom: 10, fontSize: 12, color: 'var(--text2)' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text3)', fontSize: 10, textTransform: 'uppercase' }}>Customer note: </span>
                    {order.customerNotes}
                  </div>
                )}

                {/* Unit rows */}
                {(Array.isArray(order.units) ? order.units : []).map((unit, i) => {
                  const priceKey = `${order.id}-${i}`;
                  const isRejecting = rejectingUnit?.orderId === order.id && rejectingUnit?.unitIndex === i;
                  const editPrice = editPrices[priceKey];
                  const hasPrice = unit.fob_price_usd > 0;

                  return (
                    <div key={unit.unit_id || i} style={{
                      padding: '10px 0', borderBottom: i < order.units.length - 1 ? '1px solid var(--border)' : 'none',
                      opacity: unit.status === 'rejected' ? 0.5 : 1,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ color: 'var(--text3)', flexShrink: 0, width: 20 }}>{getUnitIcon(unit.unit_type)}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{unit.unit_description}</div>
                          <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{unit.unit_sku}</div>
                          {(() => {
                            if (unit.status !== 'pending') return null;
                            const key = unit.unit_type === 'vehicle' ? 'vehicles' : unit.unit_type === 'engine' ? 'engines' : 'spareParts';
                            const ex = (DB.get(key) || []).find(u => u.id === unit.unit_id);
                            if (ex?.status === 'sold' && ex?.assignedTo !== order.customerId) {
                              return <div style={{ fontSize: 10, color: 'var(--red)', fontWeight: 700, marginTop: 2 }}>🔴 SOLD — Already approved for another customer</div>;
                            }
                            return null;
                          })()}
                          {unit.admin_notes && <div style={{ fontSize: 10, color: 'var(--red)', marginTop: 2 }}>Reason: {unit.admin_notes}</div>}
                        </div>

                        {/* Price: show or inline edit */}
                        {unit.status === 'pending' && !hasPrice ? (
                          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 10, color: 'var(--red)', fontWeight: 600 }}>NO PRICE</span>
                            <input type="number" placeholder="$" value={editPrice ?? ''}
                              onChange={e => setEditPrices(prev => ({ ...prev, [priceKey]: e.target.value }))}
                              onClick={e => e.stopPropagation()}
                              style={{ width: 70, padding: '3px 6px', fontSize: 11, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)' }} />
                          </div>
                        ) : hasPrice ? (
                          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--green)', flexShrink: 0 }}>${unit.fob_price_usd.toLocaleString()}</div>
                        ) : null}

                        {/* Actions */}
                        {unit.status === 'pending' && (
                          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            <button className="btn btn-success btn-sm" style={{ padding: '4px 10px', fontSize: 11 }}
                              onClick={() => {
                                // If price was edited inline, save it to the unit's pricing
                                if (editPrice && Number(editPrice) > 0) {
                                  const key = unit.unit_type === 'vehicle' ? 'vehicles' : unit.unit_type === 'engine' ? 'engines' : 'spareParts';
                                  const list = DB.get(key) || [];
                                  DB.set(key, list.map(u => u.id === unit.unit_id ? { ...u, pricing: { ...(u.pricing || {}), fob_price_usd: Number(editPrice) } } : u));
                                  // Also update the price in the order
                                  const updatedUnits = [...order.units];
                                  updatedUnits[i] = { ...updatedUnits[i], fob_price_usd: Number(editPrice) };
                                  const allOrders = DB.get('selectionOrders') || [];
                                  DB.set('selectionOrders', allOrders.map(o => o.id === order.id ? { ...o, units: updatedUnits } : o));
                                }
                                approveUnit(order, i);
                              }}>✓</button>
                            <button className="btn btn-secondary btn-sm" style={{ padding: '4px 10px', fontSize: 11, color: 'var(--red)', borderColor: 'var(--red)' }}
                              onClick={() => { setRejectingUnit({ orderId: order.id, unitIndex: i }); setRejectReason(''); }}>✕</button>
                          </div>
                        )}
                        {unit.status === 'approved' && <span style={{ fontSize: 10, color: 'var(--green)', fontWeight: 700, flexShrink: 0 }}>✓ Approved</span>}
                        {unit.status === 'rejected' && <span style={{ fontSize: 10, color: 'var(--red)', fontWeight: 700, flexShrink: 0 }}>✕ Rejected</span>}
                      </div>

                      {/* Inline rejection reason form */}
                      {isRejecting && (
                        <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input className="form-input" value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                            placeholder="Rejection reason (optional)" style={{ flex: 1, fontSize: 11, padding: '5px 8px' }} autoFocus />
                          <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => setRejectingUnit(null)}>Cancel</button>
                          <button className="btn btn-danger btn-sm" style={{ fontSize: 10 }} onClick={() => rejectUnit(order, i, rejectReason)}>Reject</button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Admin notes */}
                {pendingCount > 0 && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                    <label className="form-label" style={{ fontSize: 10 }}>Admin Notes (visible to customer)</label>
                    <input className="form-input" value={adminNotes[order.id] ?? order.reviewNotes ?? ''}
                      onChange={e => setAdminNotes(prev => ({ ...prev, [order.id]: e.target.value }))}
                      placeholder="Add notes for the customer…" style={{ fontSize: 12, marginBottom: 6 }} />
                    {adminNotes[order.id] !== undefined && adminNotes[order.id] !== (order.reviewNotes || '') && (
                      <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => {
                        const allOrders = DB.get('selectionOrders') || [];
                        DB.set('selectionOrders', allOrders.map(o => o.id === order.id ? { ...o, reviewNotes: adminNotes[order.id] } : o));
                        forceUpdate({});
                      }}>Save Note</button>
                    )}
                  </div>
                )}

                {/* Invoice link */}
                {order.invoiceId && (
                  <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(63,185,80,0.08)', borderRadius: 8, fontSize: 12, color: 'var(--green)' }}>
                    ✓ Proforma invoice generated — #{order.invoiceId}
                  </div>
                )}

                {/* Existing review notes */}
                {order.reviewNotes && !pendingCount && (
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text2)', fontStyle: 'italic' }}>
                    Admin note: {order.reviewNotes}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default SelectionOrders;
